import { Fragment, useCallback, useMemo, useState } from 'react';
import _ from 'lodash';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import SliderComponent from './Slider.component';
import CardMedia from './CardMedia.component';
import { v4 as uuidv4 } from 'uuid';
import ImageIcon from '@mui/icons-material/Image';
import UploadButton from '../form/components/Upload.component';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDialog from './Confirm.component';

const UploadImageProduct = ({
  item,
  editable = false,
  handleFileChange = () => {},
  handleFileDelete = () => {},
  remainingFiles,
}) => {
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const carouselHeight = '50vh';

  const attachmentsToShow = useMemo(
    () =>
      editable && remainingFiles
        ? item?.attributes?.attachment?.data?.filter((attachment) =>
            remainingFiles.includes(attachment.id)
          )
        : item?.attributes?.attachment?.data,
    [editable, remainingFiles, item]
  );
  const handleOpenImageModal = useCallback(
    (imageUrl, index = 0) => {
      setSelectedImage(imageUrl);
      setCurrentImageIndex(index);
      setOpenImageModal(true);
    },
    [setSelectedImage, setCurrentImageIndex, setOpenImageModal]
  );

  const handleCloseImageModal = useCallback(() => {
    setOpenImageModal(false);
    setSelectedImage(null);
  }, []);

  const handleOpenDeleteConfirm = (itemId, fileId, e) => {
    e.stopPropagation();
    setFileToDelete({ itemId, fileId });
    setOpenDeleteConfirm(true);
  };
  const handleCloseDeleteConfirm = () => {
    setOpenDeleteConfirm(false);
    setFileToDelete(null);
  };
  const confirmFileDelete = useCallback(() => {
    if (fileToDelete) {
      handleFileDelete(fileToDelete.itemId, fileToDelete.fileId);
    }
    setOpenDeleteConfirm(false);
  }, [fileToDelete, handleFileDelete, setOpenDeleteConfirm]);

  const getImagesUrls = () => {
    if (!attachmentsToShow || attachmentsToShow.length === 0) return [];

    const base_url = process.env.REACT_APP_BASE_URL.split('/api')[0];
    return attachmentsToShow.map((attachment) => {
      const url = _.get(attachment?.attributes, 'url');
      return `${base_url}${url}`;
    });
  };

  const imageUrls = getImagesUrls();

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? imageUrls.length - 1 : prevIndex - 1
    );
    setSelectedImage(
      imageUrls[
        currentImageIndex === 0 ? imageUrls.length - 1 : currentImageIndex - 1
      ]
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === imageUrls.length - 1 ? 0 : prevIndex + 1
    );
    setSelectedImage(
      imageUrls[
        currentImageIndex === imageUrls.length - 1 ? 0 : currentImageIndex + 1
      ]
    );
  };

  return (
    <Fragment>
      {editable && (
        <Box mb={2}>
          <UploadButton
            label='Photo'
            name={`attachment_${item.id}`}
            multiple={true}
            onChange={(files) => handleFileChange(files)}
          />
        </Box>
      )}

      {attachmentsToShow && attachmentsToShow.length > 0 ? (
        <Box mb={1} mt={1}>
          {!editable ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() =>
                attachmentsToShow.length > 0 &&
                handleOpenImageModal(imageUrls[0], 0)
              }
            >
              <ImageIcon sx={{ color: '#3f51b5' }} />
              <Typography variant='body2' sx={{ ml: 1 }}>
                {attachmentsToShow.length}{' '}
                {attachmentsToShow.length === 1 ? 'image' : 'images'}
              </Typography>
            </Box>
          ) : (
            <SliderComponent>
              {attachmentsToShow.map((attachment, index) => {
                const base_url =
                  process.env.REACT_APP_BASE_URL.split('/api')[0];
                const url = _.get(attachment?.attributes, 'url');
                const fullImageUrl = `${base_url}${url}`;

                return (
                  <Box pr={1} key={uuidv4()} sx={{ position: 'relative' }}>
                    <Box
                      onClick={() => handleOpenImageModal(fullImageUrl, index)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <CardMedia image={fullImageUrl} />
                    </Box>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 6,
                        margin: '5px',
                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 1)',
                        },
                        width: '20px',
                        height: '20px',
                      }}
                      size='small'
                      onClick={(e) =>
                        handleOpenDeleteConfirm(item.id, attachment.id, e)
                      }
                    >
                      <DeleteIcon fontSize='small' color='error' />
                    </IconButton>
                  </Box>
                );
              })}
            </SliderComponent>
          )}
        </Box>
      ) : (
        !editable && '-'
      )}

      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        maxWidth='md'
        fullWidth
      >
        <DialogContent
          sx={{ position: 'relative', padding: '16px', height: carouselHeight }}
        >
          <IconButton
            onClick={handleCloseImageModal}
            sx={{
              position: 'absolute',
              top: 3,
              right: 4,
              color: 'black',
            }}
          >
            <CloseIcon />
          </IconButton>
          {imageUrls.length > 0 && (
            <>
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={selectedImage}
                  alt='Full Size'
                  style={{
                    maxWidth: '90%',
                    maxHeight: '90%',
                    objectFit: 'contain',
                    borderRadius: '6px',
                  }}
                />
              </Box>
              {imageUrls.length > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevImage}
                    sx={{
                      position: 'absolute',
                      left: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextImage}
                    sx={{
                      position: 'absolute',
                      right: '3px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'black',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ textAlign: 'center' }} />
                  </IconButton>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: '10px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      padding: '5px 10px',
                      borderRadius: '12px',
                    }}
                  >
                    {currentImageIndex + 1} / {imageUrls.length}
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={openDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        title='Are you sure you want to delete this image?'
        description='The image will be deleted permanently. You cannot undo this action.'
        cancelAction={handleCloseDeleteConfirm}
        confirmAction={confirmFileDelete}
      />
    </Fragment>
  );
};

export default UploadImageProduct;
